import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { invisibleCharacters, visibleKeyItems } from "@/data/key-items";
import { loadPortraitUrls } from "@/data/load-book-home";
import { activeBeat, type BookTimelineLayout, type SceneBeat } from "@/lib/timeline/book/BookLayout";
import { MARKER_DIAMETER } from "@/lib/timeline/marker/MarkerMetrics/metrics";
import type { PlaybackState } from "@/lib/timeline/playback";
import { easedPovAnchor, type PovSmoothState } from "@/lib/timeline/marker/PovIndicator/smooth";
import { keyItemCenter, type Point } from "./keyItemPosition";

interface TimelineKeyItemsProps {
  canvas: HTMLCanvasElement | null;
  layout: BookTimelineLayout;
  getPlayback: () => PlaybackState;
  spoilersOn: boolean;
}

type KeyItemOnScene = NonNullable<SceneBeat["keyItems"]>[number];

const NONE: readonly KeyItemOnScene[] = [];

const sameItems = (a: readonly KeyItemOnScene[], b: readonly KeyItemOnScene[]): boolean => (
  a === b || (a.length === b.length && a.every((item, index) => item === b[index]))
);

/** Slows the POV badge's glide, so an item changing hands can be seen crossing the screen. */
const ITEM_GLIDE_FRAME_MS = 2;

/** How far below a marker's placed top its portrait's center is, measured once from the page. */
let discMiddle: number | null = null;

function discMiddleOf(marker: HTMLElement): number {
  const face = marker.querySelector<HTMLElement>(".timeline-marker__disc");
  if (face === null || face.offsetHeight === 0) {
    return MARKER_DIAMETER / 2;
  }
  discMiddle ??= face.offsetTop + face.offsetHeight / 2;
  return discMiddle;
}

/** Where a marker's disc is centered in the world, from the transform it was placed with. */
function discCenter(element: Element): Point | null {
  const marker = element as HTMLElement;
  const match = /translate\((-?[\d.]+)px, (-?[\d.]+)px\)/.exec(marker.style.transform);
  return match === null || marker.style.display === "none"
    ? null
    : { x: Number(match[1]), y: Number(match[2]) + discMiddleOf(marker) };
}

/** Someone inside a vehicle rides small on its marker, the way a key item does on a portrait. */
const aboardCache = new WeakMap<object, readonly KeyItemOnScene[]>();

function aboardMarkers(beat: SceneBeat, layout: BookTimelineLayout): readonly KeyItemOnScene[] {
  const aboard = beat.aboard ?? [];
  const known = aboardCache.get(aboard);
  if (known !== undefined) {
    return known;
  }
  const markers = aboard.map(({ characterId, vehicleId }) => ({
    itemId: characterId,
    name: layout.markers.find((marker) => marker.characterId === characterId)?.displayName
      ?? characterId,
    characterIds: [vehicleId],
  }));
  aboardCache.set(aboard, markers);
  return markers;
}

/** Key items as discs: on the portrait of whoever has one, or between everyone sharing it. */
export function TimelineKeyItems({
  canvas,
  layout,
  getPlayback,
  spoilersOn,
}: TimelineKeyItemsProps) {
  const [items, setItems] = useState<readonly KeyItemOnScene[]>(NONE);
  const [urlOf, setUrlOf] = useState<((id: string) => string | undefined) | null>(null);
  const hostRef = useRef<Element | null>(null);
  const glides = useRef(new Map<string, PovSmoothState>());

  useEffect(() => {
    if (layout.bookId !== undefined) {
      void loadPortraitUrls(layout.bookId).then((next) => setUrlOf(() => next));
    }
  }, [layout.bookId]);

  useEffect(() => {
    if (canvas === null) {
      return;
    }
    const vehicleIds = new Set(layout.rail.stays
      .filter((stay) => stay.vehicle === true)
      .map((stay) => stay.characterId));
    const onFrame = (): void => {
      hostRef.current ??= document.querySelector(".timeline-marker-layer--discs");
      const beat = activeBeat(layout, getPlayback().playheadMs);
      const items = visibleKeyItems(beat?.keyItems ?? NONE, spoilersOn);
      const next = beat?.aboard === undefined
        ? items
        : [...items, ...aboardMarkers(beat, layout)];
      setItems((current) => (sameItems(current, next) ? current : next));
      for (const id of glides.current.keys()) {
        if (!next.some((item) => item.itemId === id)) {
          glides.current.delete(id);
        }
      }
      const hidden = invisibleCharacters(next);
      const cast = [...document.querySelectorAll<HTMLElement>('[data-marker-part="disc"]')].flatMap((marker) => {
        const itemId = hidden.get(marker.dataset.characterId ?? "");
        const invisible = String(itemId !== undefined);
        if (marker.dataset.invisible !== invisible) {
          marker.dataset.invisible = invisible;
        }
        // Left in place when it comes off, so the image can fade out.
        const image = itemId === undefined ? undefined : urlOf?.(`item-${itemId}`);
        if (image !== undefined && marker.dataset.wornImage !== image) {
          marker.dataset.wornImage = image;
          marker.style.setProperty("--worn-item-image", `url("${image}")`);
        }
        const at = discCenter(marker);
        // Vehicles sit in their own row, so they are not part of the group's middle.
        return at === null || vehicleIds.has(marker.dataset.characterId ?? "") ? [] : [at];
      });
      for (const item of next) {
        const holders = item.characterIds.flatMap((id) => {
          const marker = document.querySelector(`[data-marker-part="disc"][data-character-id="${id}"]`);
          const at = marker === null ? null : discCenter(marker);
          return at === null ? [] : [at];
        });
        const el = document.querySelector<HTMLElement>(`[data-key-item="${item.itemId}"]`);
        const center = keyItemCenter(holders, cast);
        if (el === null || center === null) {
          glides.current.delete(item.itemId);
          if (el !== null) {
            el.style.display = "none";
          }
          continue;
        }
        // Changing hands, or moving to the middle of a group, glides like the POV badge.
        const shared = holders.length > 1;
        const eased = easedPovAnchor(
          glides.current.get(item.itemId) ?? null,
          { anchorX: center.x, topY: center.y },
          item.characterIds.join(),
          ITEM_GLIDE_FRAME_MS,
        );
        glides.current.set(item.itemId, eased.state);
        el.style.display = "";
        el.dataset.shared = String(shared);
        el.style.transform = `translate(${eased.anchor.anchorX}px, ${eased.anchor.topY}px) `
          + "translate(-50%, -50%)";
      }
    };
    // After every other listener, so this reads where the portraits are this frame, not last.
    const afterPortraits = (): void => queueMicrotask(onFrame);
    canvas.addEventListener("timeline-frame", afterPortraits);
    onFrame();
    return () => canvas.removeEventListener("timeline-frame", afterPortraits);
  }, [canvas, getPlayback, layout, spoilersOn, urlOf]);

  const host = hostRef.current;
  return host === null ? null : createPortal(
    items.map((item) => {
      const image = urlOf?.(`item-${item.itemId}`) ?? urlOf?.(item.itemId);
      return (
        <div
          key={item.itemId}
          data-key-item={item.itemId}
          data-glow={item.glow === true ? "true" : undefined}
          className={`timeline-key-item${item.fate === undefined ? "" : " timeline-key-item--gone"}`}
        >
          <div className="timeline-key-item__disc">
            {image === undefined ? null : <img src={image} alt="" />}
            <span>{item.name}{item.fate === undefined ? "" : `: ${item.fate.toLowerCase()}`}</span>
          </div>
        </div>
      );
    }),
    host,
  );
}
